import { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchProviders,
  createGame,
  type ProviderInfo,
  type CreateGamePayload,
} from '../../api/games';
import { getSocket } from '../../api/socket';

const STEPS = ['Provider', 'Config', 'Options', 'Review', 'Installing'];

// ── Step 1: Provider selection ─────────────────────────────────────────────

const PROVIDER_NOTES: Record<string, string> = {
  windrose:
    'Windows-only server via Wine. Requires AVX2-capable CPU (Intel Haswell 2013+ or AMD Zen 2017+ — older Xeons may not work). Players connect via invite code; no port forwarding needed. Max 4 players recommended for Early Access stability.',
  starrupture:
    'Windows-only server via Wine. Server tools are marked experimental by the developer — expect occasional instability after game updates. Visual C++ 2022 runtime is installed automatically.',
};

function ProviderStep({
  providers,
  selected,
  onSelect,
}: {
  providers: ProviderInfo[];
  selected: string;
  onSelect: (id: string) => void;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Select a game provider
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {providers.map((p) => (
          <Grid item xs={12} sm={4} key={p.id}>
            <Card
              variant="outlined"
              sx={{
                borderColor: selected === p.id ? 'primary.main' : undefined,
                bgcolor: selected === p.id ? 'action.selected' : undefined,
              }}
            >
              <CardActionArea onClick={() => onSelect(p.id)} sx={{ p: 2 }}>
                <Typography variant="h6">{p.displayName}</Typography>
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                  {p.needsWine && <Chip label="Wine" size="small" color="warning" />}
                  {p.defaultPorts.map((port) => (
                    <Chip
                      key={`${port.port}-${port.protocol}`}
                      label={`${port.port}/${port.protocol}`}
                      size="small"
                    />
                  ))}
                </Stack>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      {PROVIDER_NOTES[selected] && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {PROVIDER_NOTES[selected]}
        </Alert>
      )}
    </Box>
  );
}

// ── Step 2: Basic config ────────────────────────────────────────────────────

interface ConfigState {
  game_id: string;
  name: string;
  thumbnail_url: string;
}

function ConfigStep({
  config,
  onChange,
  provider,
}: {
  config: ConfigState;
  onChange: (c: ConfigState) => void;
  provider: ProviderInfo | undefined;
}) {
  const installDir = config.game_id ? `/opt/gameservers/${config.game_id}` : '';

  return (
    <Stack spacing={2}>
      <TextField
        label="Game ID"
        value={config.game_id}
        onChange={(e) => onChange({ ...config, game_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
        helperText="Lowercase letters, numbers, hyphens. 3–32 chars. Cannot be changed later."
        inputProps={{ maxLength: 32 }}
        required
      />
      <TextField
        label="Display Name"
        value={config.name}
        onChange={(e) => onChange({ ...config, name: e.target.value })}
        required
      />
      <TextField
        label="Install directory"
        value={installDir}
        disabled
        helperText="Fixed — locked to /opt/gameservers/<id>"
      />
      {provider && (
        <TextField
          label="Save directory"
          value={installDir ? `${installDir}/${provider.defaultSaveSubpath}` : ''}
          disabled
          helperText="Derived from provider default"
        />
      )}
      <TextField
        label="Thumbnail URL (optional)"
        value={config.thumbnail_url}
        onChange={(e) => onChange({ ...config, thumbnail_url: e.target.value })}
        helperText="HTTPS image URL (PNG/JPEG/WebP/GIF, max 2 MB)"
        type="url"
      />
    </Stack>
  );
}

// ── Step 3: Options ────────────────────────────────────────────────────────

interface OptionsState {
  auto_firewall: boolean;
  auto_backup_on_stop: boolean;
  scheduled_backup: string;
  steam_beta_branch: string;
}

function OptionsStep({
  options,
  onChange,
}: {
  options: OptionsState;
  onChange: (o: OptionsState) => void;
}) {
  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            checked={options.auto_firewall}
            onChange={(e) => onChange({ ...options, auto_firewall: e.target.checked })}
          />
        }
        label="Auto-manage firewall rules (open ports in UFW)"
      />
      <FormControlLabel
        control={
          <Switch
            checked={options.auto_backup_on_stop}
            onChange={(e) => onChange({ ...options, auto_backup_on_stop: e.target.checked })}
          />
        }
        label="Create backup before stopping server"
      />
      <TextField
        label="Scheduled backup (optional)"
        value={options.scheduled_backup}
        onChange={(e) => onChange({ ...options, scheduled_backup: e.target.value })}
        helperText='E.g. "daily 04:00". Leave empty to disable.'
        placeholder="daily 04:00"
      />
      <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2">Advanced (SteamCMD)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TextField
            fullWidth
            label="Beta branch (optional)"
            value={options.steam_beta_branch}
            onChange={(e) => onChange({ ...options, steam_beta_branch: e.target.value })}
            helperText='Leave empty for the default stable branch. E.g. "experimental".'
            placeholder="experimental"
          />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

// ── Step 4: Review ─────────────────────────────────────────────────────────

function ReviewStep({
  provider,
  config,
  options,
}: {
  provider: ProviderInfo | undefined;
  config: ConfigState;
  options: OptionsState;
}) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Review your configuration</Typography>
      {[
        ['Provider', provider?.displayName ?? '—'],
        ['Game ID', config.game_id],
        ['Name', config.name],
        ['Install dir', `/opt/gameservers/${config.game_id}`],
        ['Thumbnail', config.thumbnail_url || '(none)'],
        ['Auto firewall', options.auto_firewall ? 'Yes' : 'No'],
        ['Backup on stop', options.auto_backup_on_stop ? 'Yes' : 'No'],
        ['Scheduled backup', options.scheduled_backup || '(none)'],
      ].map(([label, value]) => (
        <Stack key={label} direction="row" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="body2">{value}</Typography>
        </Stack>
      ))}
      <Alert severity="info" sx={{ mt: 1 }}>
        Installation downloads several GB via SteamCMD and may take 5–20 minutes.
        You can close this dialog — the installation continues in the background.
      </Alert>
    </Stack>
  );
}

// ── Step 5: Live install log ────────────────────────────────────────────────

function InstallLogStep({
  gameId,
  state,
  error,
}: {
  gameId: string;
  state: 'running' | 'succeeded' | 'failed' | 'queued' | null;
  error: string | null;
}) {
  const [lines, setLines] = useState<string[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gameId) return;
    const socket = getSocket();

    const cb = (data: { game_id: string; line: string }) => {
      if (data.game_id !== gameId) return;
      setLines((prev) => [...prev.slice(-499), data.line]);
    };

    socket.emit('install:subscribe', { game_id: gameId });
    socket.on('games:install:log', cb);

    return () => {
      socket.emit('install:unsubscribe', { game_id: gameId });
      socket.off('games:install:log', cb);
    };
  }, [gameId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  return (
    <Box>
      {state === 'queued' && (
        <Alert severity="info" sx={{ mb: 1 }}>Waiting for previous installation to finish…</Alert>
      )}
      {state === 'succeeded' && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 1 }}>
          Installation complete! The game tile is now ready.
        </Alert>
      )}
      {state === 'failed' && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 1 }}>
          Installation failed: {error ?? 'Unknown error'}
        </Alert>
      )}
      {(state === 'running' || state === 'queued' || !state) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption">Installing…</Typography>
        </Stack>
      )}
      <Box
        sx={{
          height: 320,
          overflow: 'auto',
          bgcolor: '#0d0d0d',
          px: 2,
          py: 1,
          fontFamily: 'monospace',
          fontSize: '0.72rem',
          lineHeight: 1.5,
          borderRadius: 1,
        }}
      >
        {lines.length === 0 && (
          <Typography variant="caption" color="text.secondary">Waiting for log output…</Typography>
        )}
        {lines.map((line, i) => (
          <div key={i} style={{ color: '#c5c5c5', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {line}
          </div>
        ))}
        <div ref={bottomRef} />
      </Box>
    </Box>
  );
}

// ── Main wizard ─────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

const GAME_ID_REGEX = /^[a-z0-9-]{3,32}$/;

export function AddGameWizard({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [providerId, setProviderId] = useState('');
  const [config, setConfig] = useState<ConfigState>({ game_id: '', name: '', thumbnail_url: '' });
  const [options, setOptions] = useState<OptionsState>({
    auto_firewall: true,
    auto_backup_on_stop: true,
    scheduled_backup: '',
    steam_beta_branch: '',
  });
  const [installing, setInstalling] = useState(false);
  const [installedGameId, setInstalledGameId] = useState('');
  const [installError, setInstallError] = useState<string | null>(null);
  const [jobState, setJobState] = useState<'queued' | 'running' | 'succeeded' | 'failed' | null>(null);

  const { data: providerData, isLoading: loadingProviders } = useQuery({
    queryKey: ['game-providers'],
    queryFn: fetchProviders,
    enabled: open,
  });

  const providers = providerData?.providers ?? [];
  const selectedProvider = providers.find((p) => p.id === providerId);

  // Pre-fill game_id and name when a provider is selected (only if fields are still empty)
  useEffect(() => {
    if (!providerId) return;
    const p = providers.find((pr) => pr.id === providerId);
    if (!p) return;
    setConfig((c) => ({
      ...c,
      game_id: c.game_id || p.id,
      name:    c.name    || p.displayName,
    }));
  }, [providerId, providers]);

  // Poll install job status while on install step
  useEffect(() => {
    if (step !== 4 || !installedGameId) return;
    if (jobState === 'succeeded' || jobState === 'failed') return;

    const interval = setInterval(async () => {
      try {
        const { fetchInstallStatus } = await import('../../api/games');
        const status = await fetchInstallStatus(installedGameId);
        setJobState(status.state === 'aborted' ? 'failed' : status.state);
        if (status.state === 'succeeded') {
          void qc.invalidateQueries({ queryKey: ['games'] });
        }
        if (status.state === 'failed' || status.state === 'aborted') {
          setInstallError(status.error);
        }
      } catch {
        // ignore
      }
    }, 3_000);

    return () => clearInterval(interval);
  }, [step, installedGameId, jobState, qc]);

  const handleReset = () => {
    setStep(0);
    setProviderId('');
    setConfig({ game_id: '', name: '', thumbnail_url: '' });
    setOptions({ auto_firewall: true, auto_backup_on_stop: true, scheduled_backup: '', steam_beta_branch: '' });
    setInstalling(false);
    setInstalledGameId('');
    setInstallError(null);
    setJobState(null);
  };

  const handleClose = () => {
    if (step === 4 && (jobState === 'running' || jobState === 'queued' || !jobState)) {
      // Installing in background — allow closing
    }
    handleReset();
    onClose();
  };

  const canNext = (): boolean => {
    if (step === 0) return !!providerId;
    if (step === 1) return GAME_ID_REGEX.test(config.game_id) && config.name.trim().length > 0;
    return true;
  };

  const handleInstall = async () => {
    if (installing) return;
    setInstalling(true);
    setInstallError(null);

    const payload: CreateGamePayload = {
      provider_id: providerId,
      game_id: config.game_id,
      name: config.name,
      thumbnail_url: config.thumbnail_url || undefined,
      auto_firewall: options.auto_firewall,
      auto_backup_on_stop: options.auto_backup_on_stop,
      scheduled_backup: options.scheduled_backup || null,
      steam_beta_branch: options.steam_beta_branch || null,
    };

    try {
      await createGame(payload);
      setInstalledGameId(config.game_id);
      setJobState('queued');
      setStep(4);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start installation';
      setInstallError(msg);
    } finally {
      setInstalling(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center">
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Add Game Server</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {loadingProviders && step === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loadingProviders && step === 0 && (
          <ProviderStep providers={providers} selected={providerId} onSelect={setProviderId} />
        )}
        {step === 1 && (
          <ConfigStep config={config} onChange={setConfig} provider={selectedProvider} />
        )}
        {step === 2 && (
          <OptionsStep options={options} onChange={setOptions} />
        )}
        {step === 3 && (
          <ReviewStep provider={selectedProvider} config={config} options={options} />
        )}
        {step === 4 && (
          <InstallLogStep gameId={installedGameId} state={jobState} error={installError} />
        )}

        {installError && step !== 4 && (
          <Alert severity="error" sx={{ mt: 2 }}>{installError}</Alert>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
          {step < 4 && step > 0 && (
            <Button onClick={() => setStep((s) => s - 1)} disabled={installing}>Back</Button>
          )}
          {step < 3 && (
            <Button
              variant="contained"
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext()}
            >
              Next
            </Button>
          )}
          {step === 3 && (
            <Button
              variant="contained"
              color="success"
              onClick={() => void handleInstall()}
              disabled={installing}
              startIcon={installing ? <CircularProgress size={16} /> : undefined}
            >
              Install
            </Button>
          )}
          {step === 4 && (jobState === 'succeeded' || jobState === 'failed') && (
            <Button variant="contained" onClick={handleClose}>
              {jobState === 'succeeded' ? 'Done' : 'Close'}
            </Button>
          )}
          {step === 4 && (jobState === 'running' || jobState === 'queued' || !jobState) && (
            <Button onClick={handleClose}>Run in background</Button>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
